<template>
  <div class="tenant-representative">
    <p class="top-title">
      {{ $t('tenancy.tenantRepresentative') }}
    </p>
    <div
      v-if="editable"
      class="editabel input-areas"
    >
      <div
        v-for="(item, index) in accountsOfTenancy"
        :key="`tenancy_${item.id}`"
        class="content"
      >
        <div class="name">
          <InputField
            v-for="fullnameObj of getFullnameSorts"
            :key="fullnameObj.key"
            :label="!index && fullnameObj.isFirst ? $t('user.tenantName') : ''"
            :placeholder="$t(`contact.basic.${fullnameObj.label}`)"
            :readonlyAsInput="true"
            :showColon="false"
            :value="item.TenancyAccount[`contact${fullnameObj.key}`]"
          />
        </div>
        <InputField
          class="email"
          :label="!index ? $t('user.email') : ''"
          :readonlyAsInput="true"
          placeholder=""
          :showColon="false"
          :value="item.username"
          :inputTip="$t('hint.inputTip.email')"
        />
        <InputField
          :readonlyAsInput="true"
          class="phone-tenancy"
          :label="!index ? $t('contact.phone.primaryPhone') : ''"
          placeholder=""
          :showColon="false"
          :value="item.TenancyAccount.phone"
        />
        <InputField
          :readonlyAsInput="true"
          class="alt-phone-tenancy"
          :label="!index ? $t('user.altPhone') : ''"
          placeholder=""
          :showColon="false"
          :showFlag="false"
          :value="item.TenancyAccount.altPhone"
        />
      </div>

      <div
        v-for="(item, index) in accounts"
        :key="`rep_${item.id}`"
        class="content"
      >
        <div class="name">
          <InputField
            v-for="fullnameObj of getFullnameSorts"
            :key="`TR_${fullnameObj.key}`"
            :label="!(accountsOfTenancy.length + index) && fullnameObj.isFirst ? $t('user.tenantName') : ''"
            :placeholder="$t(`contact.basic.${fullnameObj.label}`)"
            :readonly="!editable || isReadOnly(item)"
            :readonlyAsInput="isReadOnly(item)"
            :showColon="false"
            :value="item.TenantProjectAccount[`contact${fullnameObj.key}`]"
            required
            @updateValue="val => updateContactAttr(`contact${fullnameObj.key}`, val, index)"
          />
        </div>
        <InputField
          :readonly="!editable || isReadOnly(item)"
          :readonlyAsInput="isReadOnly(item) || isCurrentUser(item)"
          :class="['email',{'error':duplicateEmailIndexMap[index]}]"
          :label="!(accountsOfTenancy.length + index) ? $t('user.email') : ''"
          placeholder=""
          :showColon="false"
          :value="item.username"
          :inputTip="$t('hint.inputTip.email')"
          :pattern="regex.email"
          required
          @updateValue="val => updateContactUsername(val, index)"
        />
        <InputField
          :readonly="!editable || isReadOnly(item)"
          :readonlyAsInput="isReadOnly(item)"
          :class="['phone',{'error':duplicatePhoneIndexMap[index]}]"
          :label="!(accountsOfTenancy.length + index) ? $t('contact.phone.primaryPhone') : ''"
          placeholder=""
          :showColon="false"
          :value="item.TenantProjectAccount.phone"
          required
          @updateValue="val => updateContactAttr('phone', val, index)"
        />
        <InputField
          :readonly="!editable || isReadOnly(item)"
          :readonlyAsInput="isReadOnly(item)"
          :class="['alt-phone',{'error':duplicatePhoneIndexMap[index]}]"
          :label="!(accountsOfTenancy.length + index) ? $t('user.altPhone') : ''"
          placeholder=""
          :showColon="false"
          :showFlag="false"
          :value="item.TenantProjectAccount.altPhone"
          @updateValue="val => updateContactAttr('altPhone', val, index)"
        />
        <i
          v-if="allowRemove(item)"
          class="fa fa-times"
          @click="deleteAccount(index)"
        />
      </div>
      <span
        v-if="showAddBtn"
        class="addAccount"
        @click="addAccount"
      >{{ $t('action.addContact') }}+</span>
    </div>
    <ul
      v-else
      :class="['display-table', {'creator': showInviteBtn}]"
    >
      <div class="table-header">
        <span class="serial" />
        <span class="name">{{ $t('user.tenantName') }}</span>
        <span class="email">{{ $t('user.email') }}</span>
        <span class="phone">{{ $t('contact.phone.primaryPhone') }}</span>
        <span class="other-phone">{{ $t('user.altPhone') }}</span>
        <span class="lastlogin">{{ $t('lastLogin') }}</span>
        <span class="invited-at">{{ $t('invitedAt') }}</span>
        <span
          v-show="showInviteBtn"
          class="invite"
        />
      </div>
      <li
        v-for="(item, index) in accountsOfTenancy"
        :key="`${item.id}_${index}`"
        class="table-content"
      >
        <span class="serial" />
        <span class="name">{{ displayI18nFullName(item.TenancyAccount.contactFirstName, item.TenancyAccount.contactLastName) }}</span>
        <span class="email">{{ item.username }}</span>
        <span class="phone">{{ item.TenancyAccount.phone || $t('emptyValue') }}</span>
        <span class="other-phone">{{ item.TenancyAccount.altPhone || $t('emptyValue') }}</span>
        <span class="lastlogin">{{ formatDate(item.lastLoginAt) }}</span>
        <span class="invited-at">{{ formatDate(item.TenancyAccount.invitedAt) || '-' }}</span>
        <span
          v-show="showInviteBtn"
          class="invite"
          :style="{ visibility: isCurrentUser(item) ? 'hidden' : 'visible' }"
        />
      </li>
      <li
        v-for="(item, index) in filterAccounts"
        :key="index"
        class="table-content"
      >
        <span class="serial">{{ getOrdinalFor(index + 1) }}</span>
        <span class="name">{{ displayFullName(item.TenantProjectAccount.contactFirstName, item.TenantProjectAccount.contactLastName) }}</span>
        <span class="email">{{ item.username || '--' }}</span>
        <span class="phone">{{ item.TenantProjectAccount.phone || '--' }}</span>
        <span class="other-phone">{{ item.TenantProjectAccount.altPhone || '--' }}</span>
        <span class="lastlogin">{{ formatDate(item.lastLoginAt) }}</span>
        <span class="invited-at">{{ formatDate(item.TenantProjectAccount.invitedAt) }}</span>
        <span
          v-show="showInviteBtn"
          class="invite"
          :style="{ visibility: isCurrentUser(item) ? 'hidden' : 'visible' }"
        >
          <div
            v-if="!isReadOnly(item)"
            class="invite-btn btn btn--orange"
            @click="prepareInvite(item)"
          >{{ $t('action.invite') }}</div>
        </span>
      </li>
    </ul>
    <Modal
      ref="confirm-duplicate-representative"
      :showCloseBtn="false"
      :modalId="`duplicate-representative-${+new Date()}`"
    >
      <div class="font-bold">
        {{ $t('tenantProjects.hintDuplicateRepresentative', [prepareInvitePerson.username]) }}
      </div>
      <div slot="btns">
        <button
          class="btn btn--darkblue"
          @click="closeModal"
        >
          {{ $t('action.no') }}
        </button>
        <button
          class="btn btn--orange"
          @click="doInvite"
        >
          {{ $t('action.yes') }}
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapMutations, mapGetters, mapActions } from 'vuex'
import InputField from '@/controls/InputField'
import mutationTypes from '@/store/mutation-types'
import Modal from '@/controls/Modal'
import formFullnameMixin from '@/pages/formFullnameMixin'
import dateUtil from '@/utils/dateUtil'
import * as regex from '@/utils/regex'
import ordinalUtils from '@/utils/ordinalUtils'
import { contactTypes } from '@/api/users/consts'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import { RepAccessTypes } from '@/modules/tenantProjects/contractors/consts'
import { tenantProjectStatus } from '@/utils/locale'
import notification from '@/utils/notification'
import i18n from '@/utils/i18n'
import { inviteUser } from '@/api/tenantProjects/util'
import { getContactChip } from './consts'

export default {
  name: 'TenantRepresentative',
  components: {
    InputField,
    Modal,
  },
  mixins: [formFullnameMixin],
  props: {
    editable: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      regex,
      prepareInvitePerson: '',
      emptyValue: '--',
      duplicateEmailIndexMap: new Map(),
      duplicatePhoneIndexMap: new Map(),
    }
  },
  computed: {
    ...mapGetters({'haveInvited': 'ccd/accounts'}),
    ...mapGetters('ccd', ['canAccessAllProjects', 'currentTenancy']),
    ...mapGetters('tenantProject', ['currentAccount', 'currentTenantProject', 'isNormalAccount']),
    ...mapGetters('tenantProject', {accounts: 'accountsReturnEmptyListIfEmpty'}),
    ...mapGetters('user', ['currentUser']),
    ...mapGetters('projectContractors', ['currentMainContractor', 'isMainUser']),
    showInviteBtn () {
      return [tenantProjectStatus.SUBMITTED, tenantProjectStatus.IN_PROGRESS].includes(this.currentTenantProject.status) &&
        this.accounts.some(item => !this.isReadOnly(item))
    },
    showAddBtn () {
      return this.editable && (this.canAccessAllProjects || (this.currentAccount && (this.isMainUser || this.isItemNormalAccess(this.currentAccount))))
    },
    accountsOfTenancy () {
      const allAccountsOfTenancy = lodash.get(this.currentTenancy, 'accounts', [])
      const accounts = allAccountsOfTenancy.filter(account => this.isAdmin(account) || !!account.TenancyAccount.perms['TenantProject'])
      return accounts.map(account => {
        let ref = lodash.find(account.contacts, { type: contactTypes.PHONE_NUMBER, priority: true })
        account.phone = (ref && ref.value) || this.emptyValue
        ref = lodash.find(account.contacts, { type: contactTypes.PHONE_NUMBER, priority: false })
        account.altPhone = (ref && ref.value) || this.emptyValue
        return account
      })
    },
    filterAccounts () {
      return lodash.differenceBy(this.accounts, this.accountsOfTenancy, 'username')
    },
  },
  methods: {
    ...mapMutations('tenantProject', {
      updateAccounts: mutationTypes.TENANT_PROJECT_ACCOUNTS,
    }),
    ...mapActions('tenantProject', ['loadTenantProject']),
    isAdmin (item) {
      return lodash.get(item, 'TenancyAccount.isAdmin', false)
    },
    isReadOnly (item) {
      if (this.isItemMainContractor(item)) {
        return true
      } else if (this.canAccessAllProjects) {
        return false
      } else if (this.currentAccount) {
        return this.isNormalAccount ? !this.isItemNormalAccess(item) : !this.isMainUser || this.isItemNormalAccess(item)
      }
      return true
    },
    isCurrentUser (item) {
      return lodash.get(this.currentUser, 'id') === lodash.get(item, 'id')
    },
    isItemMainContractor (item) {
      return lodash.get(item, 'TenantProjectAccount.isContractorAdmin', false)
    },
    isItemNormalAccess (item) {
      return lodash.get(item, 'TenantProjectAccount.accessType') === RepAccessTypes.NORMAL
    },
    allowRemove (item) {
      return item.id !== this.currentUser.id && !this.isReadOnly(item)
    },
    updateContactAttr (path, value, index) {
      const accountsList = lodash.cloneDeep(this.accounts)
      accountsList[index]['TenantProjectAccount'][path] = value
      this.updateAccounts({value: accountsList})
    },
    addAccount () {
      const accountsList = lodash.cloneDeep(this.accounts)
      const newAccount = getContactChip()
      if (!this.isNormalAccount) {
        newAccount.TenantProjectAccount.accessType = RepAccessTypes.CONTRACTOR
      }
      accountsList.push(newAccount)
      this.updateAccounts({value: accountsList})
    },
    deleteAccount (index) {
      const accountsList = lodash.cloneDeep(this.accounts)
      accountsList.splice(index, 1)
      this.updateAccounts({value: accountsList})
    },
    updateContactUsername (value, index) {
      const accountsList = lodash.cloneDeep(this.accounts)
      accountsList[index]['username'] = value
      delete accountsList[index].id
      this.updateAccounts({value: accountsList})
    },
    checkoutNotEqualDash () {
      let validate = true
      this.accounts.map(account => {
        if (!account.username || !account.TenantProjectAccount.contactFirstName || !account.TenantProjectAccount.contactLastName || !account.TenantProjectAccount.phone) {
          validate = false
        }
      })
      return validate
    },
    validate () {
      formUtils.resetDirty()
      this.duplicateEmailIndexMap = new Map()
      this.duplicatePhoneIndexMap = new Map()
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.tenant-representative')
      const isValidEmails = this.validateDuplicateEmail()
      const isValidPhones = this.validateDuplicatePhone()
      return fusionInputCheckUtil.isAllFieldsValid('.tenant-representative') && this.checkoutNotEqualDash() && isValidEmails && isValidPhones
    },
    validateDuplicateEmail () {
      let validate = true
      const uniqueEmailArr = new Map()
      this.accountsOfTenancy.concat(this.accounts).map((account, index) => {
        const currentUsername = account.username
        if (uniqueEmailArr.has(currentUsername) && currentUsername !== '') {
          // The only excuse for duplicated email, check https://jirapct.epam.com/jira/browse/HKLDCMS-24167
          if (this.isItemMainContractor(account)) return

          this.duplicateEmailIndexMap[index - this.accountsOfTenancy.length] = true
          validate = false
          notification.fireNotification(false, i18n.t('hint.inputTip.emailIsExisting'))
        } else {
          uniqueEmailArr.set(currentUsername, 1)
        }
      })
      return validate
    },
    validateDuplicatePhone () {
      let validate = true
      this.accounts.map((account, index) => {
        if (account.TenantProjectAccount.phone === account.TenantProjectAccount.altPhone) {
          validate = false
          if (account.TenantProjectAccount.phone) {
            this.duplicatePhoneIndexMap[index] = true
            notification.fireNotification(false, i18n.t('hint.inputTip.phoneShouldBeDifferent'))
          }
        }
      })
      return validate
    },
    getOrdinalFor (number) {
      return number + ordinalUtils.getOrdinalFor(number)
    },
    displayFullName (firstName = '', lastName = '') {
      return this.displayI18nFullName(firstName, lastName) || this.$t('emptyValue')
    },
    formatDate (time) {
      return time ? dateUtil.format(time, 'DD MMM YY HH:mm') : '-'
    },
    async invite (user) {
      // change docoument.activeElement
      document.body.focus()
      await inviteUser(this.currentTenantProject.id, user)

      await this.loadTenantProject(this.currentTenantProject.id)
    },
    validateRepresentative (user) {
      return !this.haveInvited.some(item => item.username === user.username)
    },
    async prepareInvite (item) {
      this.prepareInvitePerson = item
      if (this.validateRepresentative(item)) {
        await this.invite(item)
      } else {
        this.openModal()
      }
    },
    async doInvite () {
      await this.invite(this.prepareInvitePerson)
      this.closeModal()
    },
    openModal () {
      this.$refs['confirm-duplicate-representative'].open()
    },
    closeModal () {
      this.$refs['confirm-duplicate-representative'].close()
    },
  },
}
</script>

<style lang="scss">
.tenant-representative {
  background: #fafafa;
  padding: 20px 35px;
  .tenancy-accounts {
    margin-bottom: 20px;
  }

  .editabel {
    margin-left: 15px;
  }
  .top-title {
    font-size: 16px;
    font-weight: bold;
  }
  .content {
    display: flex;
    position: relative;
    .error-highlight {
      background: red;
    }
    .input-field label {
      margin: 0 0 5px 0;
    }
    i.fa-times {
      cursor: pointer;
      position: absolute;
      right: -25px;
      bottom: 15px;
    }
    i.fa-times:before {
      font-size: 20px;
    }
    .name, .email, .phone, .alt-phone,
    .phone-tenancy, .alt-phone-tenancy {
      display: inline-block;
      flex: 1;
      input {
        width: 100%;
      }
      margin-right: 30px;
    }
    .error input{
      background-color: #f9ecea!important;
    }
    .name {
      min-width: 230px;
      font-size: 0;
      .input-field {
        font-size: 14px;
        width: 50%;
        display: inline-block;
        .text-input {
          margin: 0;
        }
      }
      .input-field:first-of-type {
        vertical-align: bottom;
      }
    }
    .alt-phone, .alt-phone-tenancy {
      margin: 0;
    }
  }
  .addAccount {
    margin-top: 10px;
    text-decoration: underline;
    font-style: italic;
    cursor: pointer;
  }
  .display-table.creator {
    li {
      height: 60px;
      line-height: 60px;
      span {
        line-height: 1.43;
      }
    }
  }
  .display-table {
    $serial-width: 50px;
    list-style-type: none;
    margin: 0 0 0 15px;
    .table-header {
      font-size: 12px;
      height: 30px;
      color: gray;
      .name {
        font-weight: normal;
      }
    }
    .table-header, .table-content {
      display: flex;
      align-items: center;
    }
    span {
      display: inline-block;
    }
    .serial{
      font-size: 12px;
      width: $serial-width;
      min-width: $serial-width;
      max-width: $serial-width;
    }
    .name {

      padding-right: 20px;
      flex: 1.4;
      word-break: break-word;
      word-wrap: break-word;
    }
    .email {
      flex: 1.8;
      padding-right: 20px;
      word-break: break-word;
      word-wrap: break-word;
    }
     .error input{
      background-color: #f9ecea!important;
    }
    .phone, .other-phone, .lastlogin, .invited-at{
     padding-right: 20px;
     flex: 1;
     word-break: break-word;
     word-wrap: break-word;
    }
    .invite {
      line-height: 60px;
      width: 80px;
      text-align: center;
    }
    .invite-btn {
      display: inline-block;
      text-transform: capitalize;
    }

    li {
      font-size: 14px;
    }
    li:nth-child(even) {
      background: #FAFBFC;
    }
    .name {
      font-weight: bold;
    }
  }
}
</style>
